import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import api, { getUser } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Calendar,
  MapPin,
  FolderOpen,
  Loader2,
  Lock,
  Unlock,
  ListChecks,
  Headphones,
} from "lucide-react";
import { toast } from "sonner";

export default function DJDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/competitions/mine")
      .then((r) => setComps(r.data))
      .catch((err) => {
        if (err?.response?.status !== 401) {
          toast.error(err.response?.data?.detail || "Errore caricamento");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">
            <Headphones className="w-7 h-7" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/40">
              Area DJ Responsabile
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-[#FF5722] to-[#00E5FF] bg-clip-text text-transparent">
                Ciao, {user.nome}
              </span>
            </h1>
            <div className="text-white/50 text-sm mt-1">
              Le tue competizioni assegnate
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-white/60">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento...
          </div>
        )}

        {!loading && comps.length === 0 && (
          <div
            data-testid="dj-no-competitions"
            className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center text-white/70"
          >
            Nessuna competizione assegnata al momento.
            <div className="text-xs text-white/40 mt-2">
              Contatta l'organizzazione se pensi che sia un errore.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {comps.map((c) => (
            <div
              key={c.id}
              data-testid={`dj-comp-card-${c.id}`}
              className="relative bg-[#0E0E11] rounded-2xl p-5 overflow-hidden border border-white/10"
              style={{ borderTop: `4px solid ${c.border_color}` }}
            >
              <div
                className="absolute -top-14 -right-14 w-32 h-32 rounded-full blur-3xl opacity-25"
                style={{ background: c.border_color }}
              />
              <h3 className="font-bold text-lg relative">{c.name}</h3>
              <div className="space-y-1.5 text-sm text-white/70 mt-2 relative">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" style={{ color: c.border_color }} />
                  {c.location}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" style={{ color: c.border_color }} />
                  {c.start_date}
                  {c.end_date !== c.start_date && ` → ${c.end_date}`}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  {c.closed ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      <Lock className="w-3 h-3" /> Chiusa
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      <Unlock className="w-3 h-3" /> Aperta
                    </span>
                  )}
                  {c.submission_count != null && (
                    <span className="text-xs text-white/60 inline-flex items-center gap-1">
                      <ListChecks className="w-3 h-3" />
                      {c.submission_count} invii
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-4 relative">
                <Button
                  size="sm"
                  onClick={() => navigate(`/admin/competizioni/${c.id}/files`)}
                  data-testid={`dj-browse-comp-${c.id}`}
                  className="w-full rounded-full text-xs border-0 text-white"
                  style={{
                    background: `linear-gradient(135deg, ${c.border_color} 0%, #00E5FF 100%)`,
                  }}
                >
                  <FolderOpen className="w-3 h-3 mr-1" /> Sfoglia File
                </Button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
